import { useStationMode } from '@hooks/station';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState.ts';
import {
  Node as FlowNode,
  NodeChange,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  PRODUCE_HEIGHT,
  PRODUCE_WIDTH,
  clamp,
  isStackedViewport,
  readViewportSize,
} from './constants';
import {
  WorkflowEdge,
  WorkflowEdgeComponent,
  buildWorkflowEdges,
} from './edges';
import { computeDefaultPositions } from './layout';
import { nodeTypes } from './nodes';
import type {
  NodeLayout,
  NodeSize,
  Position,
  WorkflowVisualizationProps,
} from './types';

const edgeTypes = {
  workflow: WorkflowEdgeComponent,
};

const DEFAULT_PRODUCE_SIZE: NodeSize = {
  width: PRODUCE_WIDTH,
  height: PRODUCE_HEIGHT,
};

const WorkflowVisualizationInner: React.FC<WorkflowVisualizationProps> = ({
  lyrics,
  title,
  isLoadingLyrics,
  isProducing,
  songsReady,
  hasClickedProduce = false,
  onProduce,
  onLyricsChange,
  onTitleChange,
  mode = 'music',
  cards,
  countdownSeconds = null,
  showExtendedMessage = false,
}) => {
  const { avatarReady } = useSessionFeatureState();
  const isStationMode = useStationMode();
  const reactFlow = useReactFlow();
  const isImageMode = mode === 'image';
  const [viewportSize, setViewportSize] = useState(() => readViewportSize());
  const [nodes, setNodes, applyNodeChanges] = useNodesState<FlowNode<any>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([]);
  const [produceSize, setProduceSize] =
    useState<NodeSize>(DEFAULT_PRODUCE_SIZE);
  const handleProduceSizeChange = useCallback((size: NodeSize) => {
    setProduceSize((prev) => {
      const widthDelta = Math.abs(prev.width - size.width);
      const heightDelta = Math.abs(prev.height - size.height);
      if (widthDelta < 0.5 && heightDelta < 0.5) {
        return prev;
      }
      return {
        width: size.width,
        height: size.height,
      };
    });
  }, []);
  const pendingFitRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const storedPositionsRef = useRef<Record<string, Position>>({});
  const latestDefaultsRef = useRef<Record<string, NodeLayout>>({});
  const lastLayoutSignatureRef = useRef<string | null>(null);
  const prevAvatarReadyRef = useRef<boolean>(false);
  const handleNodesChange = useCallback(
    (changes: NodeChange<FlowNode<any>>[]) => {
      applyNodeChanges(changes);
      let positionsChanged = false;
      const layoutStorage = storedPositionsRef.current;

      changes.forEach((change) => {
        if (change.type === 'position' && change.position) {
          const { id, position, dragging } = change;
          layoutStorage[id] = position;
          positionsChanged = positionsChanged || !dragging;
        }
      });

      if (positionsChanged) {
        pendingFitRef.current = false;
      }
    },
    [applyNodeChanges],
  );

  const lyricsReady = !!lyrics && !isLoadingLyrics;

  useEffect(() => {
    const handleResize = () => {
      const size = readViewportSize();
      setViewportSize((prev) => {
        const widthDelta = Math.abs(prev.width - size.width);
        const heightDelta = Math.abs(prev.height - size.height);
        if (widthDelta > 40 || heightDelta > 40) {
          storedPositionsRef.current = {};
        }
        return size;
      });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const defaults = computeDefaultPositions({
      mode,
      viewport: viewportSize,
      produceSize,
      isStationMode,
    });
    const stackedView = isStackedViewport(viewportSize);

    const layoutSignature = `${mode}-${stackedView ? 'stacked' : 'default'}`;
    if (lastLayoutSignatureRef.current !== layoutSignature) {
      storedPositionsRef.current = {};
      lastLayoutSignatureRef.current = layoutSignature;
      pendingFitRef.current = true;
    }
    const layoutStorage = storedPositionsRef.current;

    const next: FlowNode<any>[] = [];

    // In Station mode, skip avatar node entirely
    if (!isStationMode) {
      const avatarLayout = defaults.avatar;
      if (avatarReady && !prevAvatarReadyRef.current) {
        delete layoutStorage.avatar;
      }
      prevAvatarReadyRef.current = avatarReady;

      const avatarDiameter =
        avatarLayout.size?.width ?? viewportSize.height * 0.16;
      let avatarPosition = layoutStorage.avatar ?? avatarLayout.position;

      if (!avatarReady) {
        const centeredX = clamp(
          viewportSize.width / 2 - avatarDiameter / 2,
          16,
          Math.max(viewportSize.width - avatarDiameter - 16, 16),
        );
        avatarPosition = { x: centeredX, y: avatarLayout.position.y };
      }
      next.push({
        id: 'avatar',
        type: 'avatar',
        position: avatarPosition,
        data: {
          layoutMode: 'desktop',
          diameter: avatarDiameter,
          avatarReady: !!avatarReady,
        },
        draggable: true,
        selectable: false,
        style: avatarLayout.size
          ? { width: avatarLayout.size.width, height: avatarLayout.size.height }
          : undefined,
      });
      layoutStorage.avatar = avatarPosition;

      if (!avatarReady) {
        setNodes(next);
        setEdges([]);
        latestDefaultsRef.current = defaults;
        return;
      }
    }

    if (!isImageMode) {
      const lyricsLayout = defaults.lyrics;
      if (lyricsLayout) {
        const storedLyrics = stackedView ? undefined : layoutStorage.lyrics;
        const lyricsPosition = storedLyrics ?? lyricsLayout.position;
        next.push({
          id: 'lyrics',
          type: 'lyrics',
          position: lyricsPosition,
          data: {
            layoutMode: 'desktop',
            lyrics,
            title,
            isLoading: isLoadingLyrics,
            onLyricsChange,
            onTitleChange,
            size: lyricsLayout.size,
          },
          draggable: true,
          style: lyricsLayout.size
            ? {
                width: lyricsLayout.size.width,
                height: lyricsLayout.size.height,
              }
            : undefined,
        });
        if (stackedView) {
          delete layoutStorage.lyrics;
        } else {
          layoutStorage.lyrics = lyricsPosition;
        }
      }
    }

    if (!isImageMode && lyricsReady) {
      const produceLayout = defaults.produce;
      if (produceLayout) {
        const storedProduce = stackedView ? undefined : layoutStorage.produce;
        const producePosition = storedProduce ?? produceLayout.position;
        next.push({
          id: 'produce',
          type: 'produce',
          position: producePosition,
          data: {
            layoutMode: 'desktop',
            onProduce,
            isProducing,
            disabled: !lyricsReady || songsReady || hasClickedProduce,
            size: produceLayout.size,
            onSizeChange: handleProduceSizeChange,
            stackedView,
          },
          draggable: true,
          style: produceLayout.size
            ? stackedView
              ? {
                  width: produceLayout.size.width,
                  height: produceLayout.size.height,
                }
              : {
                  height: produceLayout.size.height,
                }
            : undefined,
        });
        if (stackedView) {
          delete layoutStorage.produce;
        } else {
          layoutStorage.produce = producePosition;
        }

        if (
          produceLayout.size &&
          (countdownSeconds !== null || showExtendedMessage)
        ) {
          const produceWidth = produceLayout.size.width || PRODUCE_WIDTH;
          const produceCenterX = producePosition.x + produceWidth / 2;
          const estimatedCountdownWidth = 160;
          const countdownPosition = {
            x: produceCenterX - estimatedCountdownWidth / 2,
            y:
              producePosition.y +
              (produceLayout.size.height || PRODUCE_HEIGHT) +
              12,
          };
          next.push({
            id: 'countdown',
            type: 'countdown',
            position: countdownPosition,
            data: {
              layoutMode: 'desktop',
              countdownSeconds,
              showExtendedMessage,
            },
            draggable: false,
            selectable: false,
            style: {
              width: estimatedCountdownWidth,
            },
          });
        } else {
          delete layoutStorage.countdown;
        }
      }
    } else {
      delete layoutStorage.produce;
      delete layoutStorage.countdown;
    }

    cards.forEach((card) => {
      if (!card.visible) {
        delete layoutStorage[card.id];
        return;
      }

      const layout = defaults[card.id];
      if (!layout) {
        return;
      }

      const stored = layoutStorage[card.id];
      const cardPosition = stored ?? layout.position;

      next.push({
        id: card.id,
        type: 'songCard',
        position: cardPosition,
        data: { layoutMode: 'desktop', card, size: layout.size, mode },
        draggable: true,
        style: layout.size
          ? { width: layout.size.width, height: layout.size.height }
          : undefined,
      });
      layoutStorage[card.id] = cardPosition;
    });

    setNodes(next);
    next.forEach((node) => {
      layoutStorage[node.id] = node.position;
    });
    latestDefaultsRef.current = defaults;
    pendingFitRef.current = true;

    const edgesList = buildWorkflowEdges({
      nodes: next,
      cards,
      mode,
      isLoadingLyrics,
      isProducing,
      songsReady,
      stackedView,
      isStationMode,
    });
    setEdges(edgesList);
  }, [
    avatarReady,
    isStationMode,
    cards,
    handleProduceSizeChange,
    hasClickedProduce,
    isImageMode,
    isLoadingLyrics,
    isProducing,
    lyrics,
    lyricsReady,
    mode,
    onLyricsChange,
    onTitleChange,
    onProduce,
    produceSize,
    songsReady,
    title,
    viewportSize,
    countdownSeconds,
    showExtendedMessage,
  ]);

  useEffect(() => {
    if (!reactFlow || nodes.length === 0) {
      return;
    }

    const card1Node = reactFlow.getNode?.('card1');
    const card2Node = reactFlow.getNode?.('card2');
    if (!card1Node && !card2Node) {
      return;
    }

    const bounds = containerRef.current?.getBoundingClientRect();
    const viewportHeight = bounds?.height ?? viewportSize.height;
    const topMarginMin = clamp(viewportHeight * 0.1, 48, 160);
    const bottomMargin = clamp(viewportHeight * 0.1, 56, 180);
    const minGap = clamp(viewportHeight * 0.06, 36, 140);
    const fallbackHeight = clamp(viewportHeight * 0.4, 240, 420);
    const stackedView = isStackedViewport(viewportSize);
    const defaults = latestDefaultsRef.current;

    let changed = false;
    const updates: Record<string, number> = {};

    if (stackedView) {
      const stackedRowY = defaults.card1?.position.y ?? topMarginMin;
      if (card1Node && Math.abs(card1Node.position.y - stackedRowY) > 1) {
        updates.card1 = stackedRowY;
        changed = true;
      }
      if (card2Node && Math.abs(card2Node.position.y - stackedRowY) > 1) {
        updates.card2 = stackedRowY;
        changed = true;
      }
    } else {
      if (card1Node) {
        const card2Height = card2Node?.height ?? fallbackHeight;
        const card1Height = card1Node.height ?? fallbackHeight;
        const maxCard1Y = Math.max(
          topMarginMin,
          viewportHeight -
            bottomMargin -
            card1Height -
            (card2Node ? card2Height + minGap : 0),
        );
        const clampedY = clamp(card1Node.position.y, topMarginMin, maxCard1Y);
        if (Math.abs(clampedY - card1Node.position.y) > 1) {
          updates.card1 = clampedY;
          storedPositionsRef.current.card1 = {
            ...(storedPositionsRef.current.card1 ?? card1Node.position),
            y: clampedY,
          };
          changed = true;
        }
      }

      if (card2Node) {
        const hasCard1Override =
          updates.card1 !== undefined || card1Node?.position.y !== undefined;
        const measuredCard1 = reactFlow.getNode?.('card1');
        const card1Height = hasCard1Override
          ? (measuredCard1?.height ?? fallbackHeight)
          : (card1Node?.height ?? fallbackHeight);
        const card1Y =
          updates.card1 !== undefined
            ? updates.card1
            : (card1Node?.position.y ?? topMarginMin);
        const card2Height = card2Node.height ?? fallbackHeight;
        const minY = card1Y + card1Height + minGap;
        const maxY = viewportHeight - bottomMargin - card2Height;
        const clampedY = clamp(
          card2Node.position.y,
          minY,
          Math.max(minY, maxY),
        );
        if (Math.abs(clampedY - card2Node.position.y) > 1) {
          updates.card2 = clampedY;
          storedPositionsRef.current.card2 = {
            ...(storedPositionsRef.current.card2 ?? card2Node.position),
            y: clampedY,
          };
          changed = true;
        }
      }
    }

    if (changed) {
      setNodes((prev) =>
        prev.map((node) =>
          updates[node.id] !== undefined
            ? { ...node, position: { ...node.position, y: updates[node.id]! } }
            : node,
        ),
      );
    }
  }, [nodes, reactFlow, viewportSize, mode]);

  useEffect(() => {
    if (!reactFlow || (!isStationMode && !avatarReady)) {
      return;
    }

    if (!pendingFitRef.current) {
      return;
    }

    const allNodes = reactFlow.getNodes?.();
    if (!allNodes || allNodes.length === 0) {
      return;
    }

    const stackedView = isStackedViewport(viewportSize);
    const padding = stackedView ? 0.32 : 0.22;
    const rafId = window.requestAnimationFrame(() => {
      reactFlow.fitView({
        nodes: allNodes.map((node) => ({ id: node.id })),
        includeHiddenNodes: false,
        minZoom: stackedView ? 0.45 : 0.65,
        maxZoom: 1.25,
        padding,
        duration: 200,
      });
      pendingFitRef.current = false;
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [reactFlow, nodes, viewportSize, avatarReady, isStationMode]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 w-screen h-screen"
      style={{ zIndex: 20000 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView={false}
        proOptions={{ hideAttribution: true }}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnPinch={false}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        className="!w-full !h-full !bg-transparent"
      />
    </div>
  );
};

export const WorkflowVisualization: React.FC<WorkflowVisualizationProps> = (
  props,
) => (
  <ReactFlowProvider>
    <WorkflowVisualizationInner {...props} />
  </ReactFlowProvider>
);
